import React from 'react';
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { DD_MM_YYYY } from '../../pages/Tasks/TaskConstants';

const STATUS_LABELS = {
  '': 'All',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

const QUARTER_LABELS = {
  '': 'All',
  0: 'Q1',
  1: 'Q2',
  2: 'Q3',
  3: 'Q4',
};

/**
 * Senior Manager filters for Additional WFH quota inbox.
 * Employee options must come from the reportee API (not all-tenant search).
 */
const empLabel = (emp) => {
  if (!emp) return 'All reportees';
  const name =
    `${emp.firstName || ''} ${emp.lastName || ''}`.trim() ||
    emp.employeeName ||
    emp.id;
  return emp.employeeCode != null ? `${name} (${emp.employeeCode})` : name;
};

const AdditionalWfhQuotaFilters = ({ filters, setFilters, reportees = [] }) => {
  const selectedEmp = reportees.find((e) => e.id === filters.empId);

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Request date from"
              inputFormat={DD_MM_YYYY}
              value={filters.requestDateFrom ? dayjs(filters.requestDateFrom) : null}
              onChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  requestDateFrom: value ? value.format('YYYY-MM-DD') : '',
                }))
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth size="small" />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Request date to"
              inputFormat={DD_MM_YYYY}
              value={filters.requestDateTo ? dayjs(filters.requestDateTo) : null}
              minDate={
                filters.requestDateFrom ? dayjs(filters.requestDateFrom) : undefined
              }
              onChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  requestDateTo: value ? value.format('YYYY-MM-DD') : '',
                }))
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth size="small" />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="awfh-status-label" shrink>
              Status
            </InputLabel>
            <Select
              labelId="awfh-status-label"
              displayEmpty
              value={filters.status}
              label="Status"
              renderValue={(selected) => STATUS_LABELS[selected] || 'All'}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            fullWidth
            size="small"
            options={reportees}
            value={selectedEmp || null}
            getOptionLabel={(emp) => empLabel(emp)}
            isOptionEqualToValue={(option, value) =>
              String(option.id) === String(value.id)
            }
            onChange={(e, newValue) =>
              setFilters((prev) => ({
                ...prev,
                empId: newValue ? newValue.id : '',
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employee"
                placeholder="All reportees"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Quota year"
            value={filters.quotaYear}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, quotaYear: e.target.value }))
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="awfh-q-label" shrink>
              Quarter
            </InputLabel>
            <Select
              labelId="awfh-q-label"
              displayEmpty
              value={filters.quarterIndex}
              label="Quarter"
              renderValue={(selected) =>
                selected === '' || selected == null
                  ? 'All'
                  : QUARTER_LABELS[selected] || 'All'
              }
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, quarterIndex: e.target.value }))
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value={0}>Q1</MenuItem>
              <MenuItem value={1}>Q2</MenuItem>
              <MenuItem value={2}>Q3</MenuItem>
              <MenuItem value={3}>Q4</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdditionalWfhQuotaFilters;
